'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useMemo, useState } from 'react';
import { useWalletClient } from 'wagmi';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import VaultV1Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV1Recap';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useVaultV1SimulatedFinalPosition } from '@/components/VaultPage/VaultV1/hooks/useVaultV1SimulatedFinalPosition';
import { useVaultV1WithdrawLogic } from '@/components/VaultPage/VaultV1/hooks/useVaultV1WithdrawLogic';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getRequirementsErrorMessage } from '@/helpers/requirementErrors';
import { useVaultWithdrawRequirements } from '@/hooks/operation/vault/useVaultWithdrawRequirements';
import { resolveWithdrawReviewBlock } from '@/hooks/operation/vault/withdrawReviewBlock';
import { buildReviewDialogParams, type CheckoutAttemptTrackingContext } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { InterfaceOperation } from '@/operations/operations.types';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
import VaultV1OperationReviewDialog from './VaultV1OperationReviewDialog';
type WithdrawTransaction = NonNullable<ReturnType<typeof useVaultV1WithdrawLogic>['transaction']>;
interface VaultV1WithdrawReviewDialogProps {
  vaultData: Parameters<typeof useVaultV1WithdrawLogic>[0]['vaultData'];
  vaultAddress: Address;
  inputAmount: bigint;
  isMax: boolean;
  onTxSuccess: () => void;
  entryPoint: CheckoutEntryPoint;
}

/**
 * Re-runs the panel's withdraw logic, seeded with the amount and MAX choice the user reviewed.
 * `toggleDialog` keeps the element it was given, so a handle passed as a prop would never see a
 * later block; owning the logic here rebuilds the handle from every polled vault snapshot.
 */
const VaultV1WithdrawReviewDialog = t0 => {
  const $ = _c(29);
  const {
    vaultData,
    vaultAddress,
    inputAmount,
    isMax,
    onTxSuccess,
    entryPoint
  } = t0;
  let t1;
  if ($[0] !== inputAmount || $[1] !== isMax) {
    t1 = {
      amount: inputAmount,
      isMax
    };
    $[0] = inputAmount;
    $[1] = isMax;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== vaultAddress || $[5] !== vaultData) {
    t2 = {
      vaultData,
      vaultAddress,
      initialInput: t1
    };
    $[3] = t1;
    $[4] = vaultAddress;
    $[5] = vaultData;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const logic = useVaultV1WithdrawLogic(t2);
  const t3 = logic.maxWithdraw?.value;
  const t4 = logic.error.vaultDataError ?? logic.error.userVaultPositionError;
  let t5;
  if ($[7] !== logic.inputAmount || $[8] !== logic.isLoading || $[9] !== logic.isMax || $[10] !== logic.localErrorMessage || $[11] !== logic.userVaultAssets || $[12] !== t3 || $[13] !== t4) {
    t5 = resolveWithdrawReviewBlock({
      isMax: logic.isMax,
      inputAmount: logic.inputAmount,
      maxInputAmount: t3,
      isLoading: logic.isLoading,
      errorMessage: logic.localErrorMessage,
      userVaultAssets: logic.userVaultAssets,
      readError: t4
    });
    $[7] = logic.inputAmount;
    $[8] = logic.isLoading;
    $[9] = logic.isMax;
    $[10] = logic.localErrorMessage;
    $[11] = logic.userVaultAssets;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    isAwaitingPosition,
    blockedReason
  } = t5;
  const t6 = vaultData?.asset.chain.id;
  const t7 = logic.userVaultAssets ?? ZERO;
  const t8 = logic.isLoading || isAwaitingPosition;
  let t9;
  if ($[15] !== blockedReason || $[16] !== entryPoint || $[17] !== logic.buildError || $[18] !== logic.finalPosition || $[19] !== logic.inputAmount || $[20] !== logic.sdkAction || $[21] !== logic.shareToken || $[22] !== logic.transaction || $[23] !== onTxSuccess || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== vaultAddress) {
    t9 = <VaultV1WithdrawReview vaultAddress={vaultAddress} chainId={t6} inputAmount={logic.inputAmount} userPosition={t7} finalPosition={logic.finalPosition} transaction={logic.transaction} buildError={logic.buildError} sdkAction={logic.sdkAction} shareToken={logic.shareToken} isLoading={t8} blockedReason={blockedReason} onTxSuccess={onTxSuccess} entryPoint={entryPoint} />;
    $[15] = blockedReason;
    $[16] = entryPoint;
    $[17] = logic.buildError;
    $[18] = logic.finalPosition;
    $[19] = logic.inputAmount;
    $[20] = logic.sdkAction;
    $[21] = logic.shareToken;
    $[22] = logic.transaction;
    $[23] = onTxSuccess;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = vaultAddress;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};
interface VaultV1WithdrawReviewProps {
  vaultAddress: Address;
  chainId: ChainId | undefined;
  inputAmount: bigint;
  userPosition: bigint;
  finalPosition: bigint | undefined;
  transaction: WithdrawTransaction | undefined;
  buildError: unknown;
  sdkAction: string;
  shareToken?: {
    decimals: number;
    symbol?: string;
  } | null;
  isLoading: boolean;
  /** The refreshed panel logic rejects the reviewed request; shown as the error. */
  blockedReason: string | undefined;
  onTxSuccess: () => void;
  entryPoint: CheckoutEntryPoint;
}
const VaultV1WithdrawReview = t0 => {
  const $ = _c(72);
  const {
    vaultAddress,
    chainId,
    inputAmount,
    userPosition,
    finalPosition,
    transaction,
    buildError,
    sdkAction,
    shareToken,
    isLoading,
    blockedReason,
    onTxSuccess,
    entryPoint
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    targetChain,
    account
  } = useWeb3Context();
  const {
    data: client
  } = useWalletClient();
  const [reviewSessionId] = useState(_temp);
  let t1;
  if ($[0] !== transaction || $[1] !== vaultAddress) {
    t1 = {
      label: "vaultV1Withdraw",
      vaultAddress,
      transaction
    };
    $[0] = transaction;
    $[1] = vaultAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    requirements,
    requirementsError,
    isLoading: isRequirementsLoading,
    isBridged: isRequirementsStale
  } = useVaultWithdrawRequirements(t1);
  const isPreparing = isLoading || isRequirementsLoading;
  let t2;
  if ($[3] !== account.isSafe || $[4] !== entryPoint || $[5] !== requirements || $[6] !== reviewSessionId) {
    t2 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint,
      operationTypes: [InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw],
      isSafe: account.isSafe,
      requirements
    });
    $[3] = account.isSafe;
    $[4] = entryPoint;
    $[5] = requirements;
    $[6] = reviewSessionId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const reviewTrackingParams = t2;
  let t3;
  if ($[8] !== account.address || $[9] !== requirements || $[10] !== targetChain.id) {
    t3 = tryBuildSimulationAuthorizations({
      requirements,
      chainId: targetChain.id,
      from: account.address
    });
    $[8] = account.address;
    $[9] = requirements;
    $[10] = targetChain.id;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t3;
  const t4 = buildError ?? requirementsError ?? simulationApprovalError;
  let t5;
  if ($[12] !== chainId || $[13] !== reviewSessionId || $[14] !== sdkAction || $[15] !== t4) {
    t5 = {
      sdkAction,
      chainId,
      reviewSessionId,
      error: t4
    };
    $[12] = chainId;
    $[13] = reviewSessionId;
    $[14] = sdkAction;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useLogSdkBuildFailed(t5);
  let t6;
  if ($[17] !== chainId || $[18] !== finalPosition || $[19] !== isPreparing || $[20] !== simulationAuthorizations || $[21] !== transaction || $[22] !== vaultAddress) {
    t6 = {
      vaultAddress,
      chainId,
      transaction,
      simulationAuthorizations,
      fallbackPosition: finalPosition,
      isPreparing
    };
    $[17] = chainId;
    $[18] = finalPosition;
    $[19] = isPreparing;
    $[20] = simulationAuthorizations;
    $[21] = transaction;
    $[22] = vaultAddress;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition,
    status: simulationStatus,
    isRefreshing: isSimulationRefreshing
  } = useVaultV1SimulatedFinalPosition(t6);
  let t7;
  if ($[24] !== account || $[25] !== blockedReason || $[26] !== client || $[27] !== displayedFinalPosition || $[28] !== inputAmount || $[29] !== isRequirementsLoading || $[30] !== isRequirementsStale || $[31] !== isUnverifiedFinalPosition || $[32] !== onTxSuccess || $[33] !== requirements || $[34] !== requirementsError || $[35] !== shareToken || $[36] !== simulationApprovalError || $[37] !== simulationStatus || $[38] !== submitTransactions || $[39] !== targetChain || $[40] !== toggleDialog || $[41] !== transaction || $[42] !== userPosition || $[43] !== vaultAddress) {
    t7 = checkoutTracking => {
      if (!transaction || isDefined(blockedReason) || !client || !account.isWalletTypeResolved || isRequirementsLoading || isRequirementsStale || requirements === undefined || requirementsError !== null && requirementsError !== undefined || simulationStatus === "blacklist" || simulationApprovalError !== undefined) {
        return;
      }
      submitTransactions(client, {
        actionType: InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw,
        flow: "vaultV1Withdraw",
        transaction,
        ...getSdkTracking(transaction),
        requirements,
        onTxSuccess,
        chain: targetChain,
        account,
        checkoutTracking
      }).catch(error => {
        logger.error(error instanceof Error ? error : new Error(String(error)), {
          tags: {
            component: "VaultV1WithdrawReviewDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage="Vault Withdraw" token={shareToken} operationContent={<VaultV1Recap type="withdraw" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isSimulationFallback={isUnverifiedFinalPosition} />} />, {
        closable: false
      });
    };
    $[24] = account;
    $[25] = blockedReason;
    $[26] = client;
    $[27] = displayedFinalPosition;
    $[28] = inputAmount;
    $[29] = isRequirementsLoading;
    $[30] = isRequirementsStale;
    $[31] = isUnverifiedFinalPosition;
    $[32] = onTxSuccess;
    $[33] = requirements;
    $[34] = requirementsError;
    $[35] = shareToken;
    $[36] = simulationApprovalError;
    $[37] = simulationStatus;
    $[38] = submitTransactions;
    $[39] = targetChain;
    $[40] = toggleDialog;
    $[41] = transaction;
    $[42] = userPosition;
    $[43] = vaultAddress;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  const handleContinue = t7;
  let t8;
  if ($[45] !== account.isWalletTypeResolved || $[46] !== account.isWrongChain || $[47] !== blockedReason || $[48] !== client || $[49] !== isRequirementsLoading || $[50] !== requirements || $[51] !== requirementsError || $[52] !== simulationApprovalError || $[53] !== transaction) {
    t8 = !transaction || isDefined(blockedReason) || !client || !account.isWalletTypeResolved || account.isWrongChain || isRequirementsLoading || requirements === undefined || requirementsError !== null && requirementsError !== undefined || simulationApprovalError !== undefined;
    $[45] = account.isWalletTypeResolved;
    $[46] = account.isWrongChain;
    $[47] = blockedReason;
    $[48] = client;
    $[49] = isRequirementsLoading;
    $[50] = requirements;
    $[51] = requirementsError;
    $[52] = simulationApprovalError;
    $[53] = transaction;
    $[54] = t8;
  } else {
    t8 = $[54];
  }
  const t9 = isRequirementsStale || isSimulationRefreshing;
  let t10;
  if ($[55] !== blockedReason || $[56] !== requirementsError || $[57] !== simulationApprovalError) {
    t10 = blockedReason ?? (requirementsError ? getRequirementsErrorMessage(requirementsError) : simulationApprovalError ? getRequirementsErrorMessage(simulationApprovalError) : undefined);
    $[55] = blockedReason;
    $[56] = requirementsError;
    $[57] = simulationApprovalError;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  let t11;
  if ($[59] !== displayedFinalPosition || $[60] !== handleContinue || $[61] !== inputAmount || $[62] !== isPreparing || $[63] !== isUnverifiedFinalPosition || $[64] !== reviewTrackingParams || $[65] !== simulationStatus || $[66] !== t10 || $[67] !== t8 || $[68] !== t9 || $[69] !== userPosition || $[70] !== vaultAddress) {
    t11 = <VaultV1OperationReviewDialog type="withdraw" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isButtonDisabled={t8} isLoading={isPreparing} isRefreshing={t9} handleContinue={handleContinue} reviewTrackingParams={reviewTrackingParams} simulationStatus={simulationStatus} isUnverifiedFinalPosition={isUnverifiedFinalPosition} errorReason={t10} />;
    $[59] = displayedFinalPosition;
    $[60] = handleContinue;
    $[61] = inputAmount;
    $[62] = isPreparing;
    $[63] = isUnverifiedFinalPosition;
    $[64] = reviewTrackingParams;
    $[65] = simulationStatus;
    $[66] = t10;
    $[67] = t8;
    $[68] = t9;
    $[69] = userPosition;
    $[70] = vaultAddress;
    $[71] = t11;
  } else {
    t11 = $[71];
  }
  return t11;
};
export default VaultV1WithdrawReviewDialog;
function _temp() {
  return crypto.randomUUID();
}